import React, { useCallback, useEffect, useRef, useState } from 'react';
import { catalogRoomsDelete, catalogRoomsList, catalogRoomsUpsert, catalogTranslateRoom, CatalogRoomGroup } from '../services/apiService';

/**
 * Master Admin → Standard rooms (2026-10-15): the rooms a new family picks from in the setup.
 * "Standard tasks use it as" links a room to the standard tasks' suggested room (a Laundry Room
 * gets the "Washing Room" tasks). Families get their own rooms, so edits here never change an
 * existing family's rooms — only future setups.
 */
const LANGS = ['en', 'da', 'sv', 'de', 'nb', 'fr', 'fi', 'es', 'fo', 'is', 'pl'];
const input = 'w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600';

type Edit = {
  group_id?: string; room_key: string; language_code: string; name: string;
  task_room: string; is_child_room: boolean; allow_multiple: boolean; sort_order: number;
};
const empty = (): Edit => ({ room_key: '', language_code: 'en', name: '', task_room: '', is_child_room: false, allow_multiple: false, sort_order: 0 });

const GlobalCatalogRooms: React.FC = () => {
  const [groups, setGroups] = useState<CatalogRoomGroup[]>([]);
  const [editing, setEditing] = useState<Edit | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const stop = useRef(false);

  const load = useCallback(async () => {
    try { setGroups(await catalogRoomsList()); } catch (e: any) { setMessage(e?.message || 'Could not load'); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const nameIn = (g: CatalogRoomGroup, lang: string) => g.room_templates.find((t) => t.language_code === lang)?.name;
  const taskRooms = Array.from(new Set(groups.map((g) => g.task_room).filter((x): x is string => !!x))).sort();
  const open = (g: CatalogRoomGroup, lang = 'en') => setEditing({
    group_id: g.id, room_key: g.room_key, language_code: lang, name: nameIn(g, lang) || '',
    task_room: g.task_room || '', is_child_room: g.is_child_room, allow_multiple: g.allow_multiple, sort_order: g.sort_order,
  });

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await catalogRoomsUpsert({ ...editing });
      setMessage('Saved. Existing families keep their rooms; future setups get this version.');
      setEditing(null);
      await load();
    } catch (e: any) { setMessage(e?.message || 'Could not save'); } finally { setBusy(false); }
  };

  const translateOne = async (g: CatalogRoomGroup) => {
    const r = await catalogTranslateRoom(g.id);
    return r.failed.length ? `${nameIn(g, 'en')} (${r.failed.join(', ')})` : null;
  };
  const translateMissing = async () => {
    const todo = groups.filter((g) => nameIn(g, 'en') && LANGS.some((l) => !nameIn(g, l)));
    if (!todo.length) { setMessage('Every standard room already has all languages.'); return; }
    if (!window.confirm(`Translate ${todo.length} rooms into the missing languages?`)) return;
    setBusy(true); stop.current = false;
    const failed: string[] = [];
    for (let i = 0; i < todo.length && !stop.current; i++) {
      setMessage(`Translating… ${i} of ${todo.length}`);
      try { const f = await translateOne(todo[i]); if (f) failed.push(f); } catch (e: any) { failed.push(`${nameIn(todo[i], 'en')}: ${e?.message}`); }
    }
    setMessage(`Done (marked "needs review").${failed.length ? ` Failed: ${failed.join('; ')}` : ''}`);
    setBusy(false);
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Standard rooms</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">The rooms a new family can pick in the setup.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="px-4 py-2 rounded border" disabled={busy} onClick={translateMissing}>Translate all missing</button>
          {busy && <button type="button" className="px-4 py-2 rounded border" onClick={() => { stop.current = true; }}>Stop</button>}
          <button type="button" className="px-4 py-2 rounded bg-blue-600 text-white" onClick={() => setEditing(empty())}>+ New standard room</button>
        </div>
      </div>
      {message && <p className="text-sm rounded bg-gray-100 dark:bg-gray-800 px-3 py-2">{message}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left border-b dark:border-gray-700">
            <th className="py-2">Room (English)</th><th>Standard tasks use it as</th><th>Child's own room</th><th>More than one</th><th>Languages</th><th />
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const review = g.room_templates.filter((t) => t.needs_review).length;
            return (
              <tr key={g.id} className="border-b dark:border-gray-800">
                <td className="py-2">{nameIn(g, 'en') || g.room_key}</td>
                <td>{g.task_room || '—'}</td>
                <td>{g.is_child_room ? '✓' : ''}</td>
                <td>{g.allow_multiple ? '✓' : ''}</td>
                <td>
                  {g.room_templates.length}/{LANGS.length}
                  {review > 0 && <span className="ml-2 rounded bg-amber-100 text-amber-900 px-2 py-0.5">{review} to review</span>}
                </td>
                <td className="text-right space-x-2 whitespace-nowrap">
                  <button type="button" className="underline" onClick={() => open(g)}>Edit</button>
                  <button type="button" className="underline" disabled={busy} onClick={async () => {
                    setBusy(true);
                    try { const f = await translateOne(g); setMessage(f ? `Failed: ${f}` : 'Translated (marked "needs review").'); await load(); }
                    catch (e: any) { setMessage(e?.message || 'Could not translate'); } finally { setBusy(false); }
                  }}>Translate to all languages</button>
                  <button type="button" className="underline text-red-700" onClick={async () => {
                    if (!window.confirm(`Hide "${nameIn(g, 'en')}" from future setups? Families keep their rooms.`)) return;
                    await catalogRoomsDelete(g.id); await load();
                  }}>Hide</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" role="dialog" aria-modal="true">
          <div className="bg-white dark:bg-gray-900 rounded-lg p-5 w-full max-w-lg space-y-3">
            <h3 className="text-lg font-semibold">{editing.group_id ? 'Edit standard room' : 'New standard room'}</h3>
            {editing.group_id && (
              <label className="block text-sm">Language
                <select className={input} value={editing.language_code} onChange={(e) => {
                  const g = groups.find((x) => x.id === editing.group_id);
                  if (g) open(g, e.target.value);
                }}>
                  {LANGS.map((l) => <option key={l} value={l}>{l}{groups.find((x) => x.id === editing.group_id)?.room_templates.some((t) => t.language_code === l) ? '' : ' (missing)'}</option>)}
                </select>
              </label>
            )}
            <label className="block text-sm">Name<input className={input} value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></label>
            <label className="block text-sm">Standard tasks use it as
              <input className={input} list="task-rooms" value={editing.task_room} placeholder="e.g. Kitchen (empty = its English name)"
                onChange={(e) => setEditing({ ...editing, task_room: e.target.value })} />
              <datalist id="task-rooms">{taskRooms.map((r) => <option key={r} value={r} />)}</datalist>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.is_child_room} onChange={(e) => setEditing({ ...editing, is_child_room: e.target.checked })} />Child's own room</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.allow_multiple} onChange={(e) => setEditing({ ...editing, allow_multiple: e.target.checked })} />More than one</label>
              <label className="block text-sm">Order<input type="number" className={input} value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value, 10) || 0 })} /></label>
            </div>
            <p className="text-xs text-gray-500">"Standard tasks use it as" and the checkboxes are shared by all languages. Changing the English name marks the other languages for review.</p>
            <div className="flex justify-end gap-2">
              <button type="button" className="px-4 py-2 rounded border" onClick={() => setEditing(null)}>Cancel</button>
              <button type="button" className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50" disabled={busy || !editing.name.trim()} onClick={save}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalCatalogRooms;
